import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { Message } from '@arco-design/web-vue'
import { useQueryClient } from '@tanstack/vue-query'
import type { MyStudyItem, RoleLevel } from '@/api/dashboard.api'
import {
  getMyStudies,
  updateLastVisited,
  verifyStudyPermission,
} from '@/api/dashboard.api'
import { getSites, type SiteResponse } from '@/api/site.api'
import { useAuthStore } from './auth'

const STORAGE_KEY_STUDY = 'clinwise_current_study'
const STORAGE_KEY_ROLE = 'clinwise_current_role'
const STORAGE_KEY_SITE = 'clinwise_current_site'
const STORAGE_KEY_STUDIES_CACHE = 'clinwise_studies_cache'
const STORAGE_KEY_SITES_CACHE = 'clinwise_sites_cache'
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

const normalizeUserId = (value: unknown): string | null => {
  if (!value) {
    return null
  }
  return String(value)
}

// UUID 格式验证（用于验证 studyId）
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isValidUUID = (str: string | null): boolean => {
  if (!str) return false
  return UUID_REGEX.test(str)
}

/**
 * 初始化时清理 localStorage 中无效的 studyId
 * 这可以防止旧的无效数据影响新的会话
 */
const cleanupInvalidStorageValues = () => {
  try {
    const storedStudyId = localStorage.getItem(STORAGE_KEY_STUDY)
    if (storedStudyId && !isValidUUID(storedStudyId)) {
      console.warn('[StudyContext] Cleaning up invalid studyId from localStorage:', storedStudyId)
      localStorage.removeItem(STORAGE_KEY_STUDY)
      localStorage.removeItem(STORAGE_KEY_ROLE)
      localStorage.removeItem(STORAGE_KEY_SITE)
    }
  } catch (e) {
    console.warn('[StudyContext] Failed to cleanup localStorage:', e)
  }
}

// 在模块加载时立即清理无效值
cleanupInvalidStorageValues()

/**
 * 角色信息（用于角色选择器）
 */
export interface RoleInfo {
  memberId: string // study_member.id
  roleCode: string
  roleDisplay: string
  roleLevel: RoleLevel
  memberCreatedAt: string
  authorizedCenters: string[]
}

/**
 * 中心信息（用于中心选择器）
 */
export interface SiteInfo {
  id: string
  siteCode: string
  organizationName?: string
}

interface StudiesCachePayload {
  timestamp: number
  data: MyStudyItem[]
}

interface SitesCachePayload {
  timestamp: number
  data: SiteInfo[]
}

type SitesCacheMap = Record<string, SitesCachePayload>

export const useStudyContextStore = defineStore('studyContext', () => {
  const authStore = useAuthStore()
  const activeUserId = ref<string | null>(null)

  const saveToStorage = (key: string, value: string) => {
    try {
      localStorage.setItem(key, value)
    } catch (e) {
      console.warn(`Failed to save to localStorage (${key}):`, e)
    }
  }

  const getFromStorage = (key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch (e) {
      console.warn(`Failed to read from localStorage (${key}):`, e)
      return null
    }
  }

  const removeFromStorage = (key: string) => {
    try {
      localStorage.removeItem(key)
    } catch (e) {
      console.warn(`Failed to remove from localStorage (${key}):`, e)
    }
  }

  const readJsonFromStorage = <T>(key: string): T | null => {
    const raw = getFromStorage(key)
    if (!raw) return null
    try {
      return JSON.parse(raw) as T
    } catch (e) {
      console.warn(`Failed to parse localStorage JSON (${key}):`, e)
      removeFromStorage(key)
      return null
    }
  }

  const writeJsonToStorage = (key: string, value: unknown) => {
    try {
      localStorage.setItem(key, JSON.stringify(value))
    } catch (e) {
      console.warn(`Failed to write localStorage JSON (${key}):`, e)
    }
  }

  const readValidUuidFromStorage = (key: string): string | null => {
    const value = getFromStorage(key)
    if (!value) return null
    if (!isValidUUID(value)) {
      removeFromStorage(key)
      return null
    }
    return value
  }

  const getStoredUserId = (): string | number | null => {
    try {
      const userStr = localStorage.getItem('user')
      if (!userStr) return null
      const parsed = JSON.parse(userStr)
      return parsed?.id ?? parsed?.sub ?? null
    } catch (_e) {
      return null
    }
  }

  const initialUserId = getStoredUserId()
  activeUserId.value = normalizeUserId(authStore.user?.id ?? initialUserId)

  const clearUserCacheFromStorage = (userId?: string | null) => {
    const targetUserId = normalizeUserId(userId ?? activeUserId.value)
    if (!targetUserId) {
      return
    }
    removeFromStorage(`${STORAGE_KEY_STUDIES_CACHE}:${targetUserId}`)
    removeFromStorage(`${STORAGE_KEY_SITES_CACHE}:${targetUserId}`)
  }

  const clearInMemoryState = () => {
    availableStudies.value = []
    isLoading.value = false
    lastCacheTime.value = null
    currentStudyId.value = null
    currentRoleMemberId.value = null
    currentSiteId.value = null
    sitesCache.value.clear()
    sitesCacheTime.value.clear()
  }

  const setActiveUserId = (userId: string | null) => {
    activeUserId.value = userId
  }
  const getCacheKey = (baseKey: string, userId?: string | null) => {
    const targetUserId = normalizeUserId(userId ?? authStore.user?.id ?? initialUserId)
    return targetUserId ? `${baseKey}:${targetUserId}` : baseKey
  }

  const cachedStudies = readJsonFromStorage<StudiesCachePayload>(
    getCacheKey(STORAGE_KEY_STUDIES_CACHE, activeUserId.value)
  )
  const cachedSites = readJsonFromStorage<SitesCacheMap>(
    getCacheKey(STORAGE_KEY_SITES_CACHE, activeUserId.value)
  )

  // State
  const availableStudies = ref<MyStudyItem[]>(cachedStudies?.data || [])
  const isLoading = ref(false)
  const lastCacheTime = ref<number | null>(cachedStudies?.timestamp || null)

  // 当前选择的研究（studyId）
  const currentStudyId = ref<string | null>(readValidUuidFromStorage(STORAGE_KEY_STUDY))
  // 当前选择的角色（memberId）
  const currentRoleMemberId = ref<string | null>(readValidUuidFromStorage(STORAGE_KEY_ROLE))
  // 当前选择的中心（siteId）
  const currentSiteId = ref<string | null>(readValidUuidFromStorage(STORAGE_KEY_SITE))

  // 中心数据缓存
  const sitesCache = ref<Map<string, SiteInfo[]>>(new Map())
  const sitesCacheTime = ref<Map<string, number>>(new Map())

  if (cachedSites) {
    Object.entries(cachedSites).forEach(([studyId, entry]) => {
      if (entry && Array.isArray(entry.data)) {
        sitesCache.value.set(studyId, entry.data)
        if (typeof entry.timestamp === 'number') {
          sitesCacheTime.value.set(studyId, entry.timestamp)
        }
      }
    })
  }

  // Query client for cache invalidation
  const queryClient = useQueryClient()

  // ============ Computed: 当前研究相关 ============

  // 安全的 currentStudyId getter - 只返回有效的 UUID
  const safeCurrentStudyId = computed(() => {
    const id = currentStudyId.value
    if (!id) return null
    if (!isValidUUID(id)) {
      console.error('[StudyContext] Invalid currentStudyId detected:', id)
      return null
    }
    return id
  })

  // 当前研究的所有成员记录（用户可能在同一研究下有多个角色）
  const currentStudyMembers = computed(() => {
    if (!safeCurrentStudyId.value) {
      console.log('[StudyContext] currentStudyMembers: no valid currentStudyId')
      return []
    }
    const members = availableStudies.value.filter(s => s.studyId === safeCurrentStudyId.value)
    console.log('[StudyContext] currentStudyMembers computed:', {
      currentStudyId: safeCurrentStudyId.value,
      totalStudies: availableStudies.value.length,
      matchedMembers: members.length,
    })
    return members
  })

  // 当前研究信息（取第一个成员记录的 study 信息）
  const currentStudy = computed(() => {
    const firstMember = currentStudyMembers.value[0]
    return firstMember || null
  })

  const currentStudyCode = computed(() => currentStudy.value?.study.code || null)
  const currentStudyTitle = computed(() => currentStudy.value?.study.title || null)
  const hasCurrentStudy = computed(() => currentStudy.value !== null)

  // 用户可选择的研究列表（按 studyId 去重）
  const uniqueStudies = computed(() => {
    const studyMap = new Map<string, MyStudyItem>()
    for (const item of availableStudies.value) {
      if (!studyMap.has(item.studyId)) {
        studyMap.set(item.studyId, item)
      }
    }
    return Array.from(studyMap.values())
  })

  const hasMultipleStudies = computed(() => uniqueStudies.value.length > 1)

  // ============ Computed: 当前角色相关 ============

  // 当前研究下可选的角色列表（按 memberCreatedAt 升序）
  const availableRoles = computed<RoleInfo[]>(() => {
    return currentStudyMembers.value
      .map(m => ({
        memberId: m.id,
        roleCode: m.roleCode,
        roleDisplay: m.roleDisplay,
        roleLevel: m.roleLevel,
        memberCreatedAt: m.memberCreatedAt,
        authorizedCenters: m.authorizedCenters,
      }))
      .sort((a, b) => new Date(a.memberCreatedAt).getTime() - new Date(b.memberCreatedAt).getTime())
  })

  // 当前选中的角色
  const currentRole = computed<RoleInfo | null>(() => {
    if (!currentRoleMemberId.value) return null
    return availableRoles.value.find(r => r.memberId === currentRoleMemberId.value) || null
  })

  const currentRoleCode = computed(() => currentRole.value?.roleCode || null)
  const currentRoleDisplay = computed(() => currentRole.value?.roleDisplay || null)
  const currentRoleLevel = computed(() => currentRole.value?.roleLevel || null)
  const hasMultipleRoles = computed(() => availableRoles.value.length > 1)

  // 是否是中心级角色
  const isSiteLevel = computed(() => currentRole.value?.roleLevel === 'SITE')

  // ============ Computed: 当前中心相关 ============

  // 当前角色授权的中心 ID 列表
  const authorizedCenterIds = computed(() => currentRole.value?.authorizedCenters || [])

  // 当前研究下可选的中心列表
  const availableSites = computed<SiteInfo[]>(() => {
    console.log('[StudyContext] availableSites computed:', {
      currentStudyId: safeCurrentStudyId.value,
      isSiteLevel: isSiteLevel.value,
      authorizedCenterIds: authorizedCenterIds.value,
    })
    if (!safeCurrentStudyId.value || !isSiteLevel.value) return []
    const cached = sitesCache.value.get(safeCurrentStudyId.value)
    console.log('[StudyContext] sitesCache for study:', cached?.length || 0)
    if (!cached) return []
    // 过滤出授权的中心（使用字符串比较，site.id 和 authorizedCenters 都是 UUID 字符串）
    const authorizedIds = authorizedCenterIds.value.map(id => String(id))
    const filtered = cached.filter(site => authorizedIds.includes(site.id))
    console.log('[StudyContext] filtered availableSites:', filtered.length)
    return filtered
  })

  // 当前选中的中心
  const currentSite = computed<SiteInfo | null>(() => {
    if (!currentSiteId.value) return null
    return availableSites.value.find(s => s.id === currentSiteId.value) || null
  })

  const currentSiteCode = computed(() => currentSite.value?.siteCode || null)
  const hasMultipleSites = computed(() => availableSites.value.length > 1)

  // ============ Actions ============

  /**
   * 加载用户可用的研究列表
   */
  const refreshAvailableStudies = async (showLoading: boolean) => {
    try {
      if (showLoading) {
        isLoading.value = true
      }
      const response = await getMyStudies({
        // 方案B：BFF 模式下不传 userId，由后端从会话/JWT 解析
        page: 1,
        pageSize: 100,
        sortBy: 'lastVisitedAt',
        sortOrder: 'desc',
      })

      availableStudies.value = response.data.list
      lastCacheTime.value = Date.now()
      writeJsonToStorage(getCacheKey(STORAGE_KEY_STUDIES_CACHE, activeUserId.value), {
        timestamp: lastCacheTime.value,
        data: availableStudies.value,
      })

      console.log(`[StudyContext] Loaded ${availableStudies.value.length} study members`)
    } catch (error: any) {
      console.error('[StudyContext] Failed to load available studies:', error)
      Message.error('加载研究列表失败')
    } finally {
      if (showLoading) {
        isLoading.value = false
      }
    }
  }

  const loadAvailableStudies = async (forceRefresh = false) => {
    console.log('[StudyContext] loadAvailableStudies called, forceRefresh:', forceRefresh)

    if (!authStore.user?.id) {
      console.warn('[StudyContext] No user found, cannot load studies')
      return
    }

    const hasCache = availableStudies.value.length > 0
    const isFresh = !!lastCacheTime.value && Date.now() - lastCacheTime.value < CACHE_TTL

    if (!forceRefresh && hasCache) {
      if (!isFresh) {
        void refreshAvailableStudies(false)
      }
      console.log('[StudyContext] Using cached studies, count:', availableStudies.value.length)
      return
    }

    await refreshAvailableStudies(true)
  }

  /**
   * 加载研究的中心列表
   */
  const refreshSitesForStudy = async (studyId: string) => {
    try {
      const response = await getSites({
        studyId,
        status: 'ACTIVE',
        type: 'RESEARCH_CENTER',
        pageSize: 100,
      })

      const sites: SiteInfo[] = response.data.map((site: SiteResponse) => ({
        id: site.id,
        siteCode: site.siteCode,
        organizationName: site.organizationName,
      }))

      sitesCache.value.set(studyId, sites)
      sitesCacheTime.value.set(studyId, Date.now())

      const payload: SitesCacheMap = {}
      for (const [cacheStudyId, cachedSites] of sitesCache.value.entries()) {
        const timestamp = sitesCacheTime.value.get(cacheStudyId) || Date.now()
        payload[cacheStudyId] = { timestamp, data: cachedSites }
      }
      writeJsonToStorage(getCacheKey(STORAGE_KEY_SITES_CACHE, activeUserId.value), payload)

      return sites
    } catch (error) {
      console.error('[StudyContext] Failed to load sites:', error)
      return []
    }
  }

  const loadSitesForStudy = async (studyId: string, forceRefresh = false) => {
    const cachedSitesForStudy = sitesCache.value.get(studyId)
    const cachedAt = sitesCacheTime.value.get(studyId)
    const isFresh = !!cachedAt && Date.now() - cachedAt < CACHE_TTL

    if (cachedSitesForStudy && !forceRefresh) {
      if (!isFresh) {
        void refreshSitesForStudy(studyId)
      }
      return cachedSitesForStudy
    }

    return await refreshSitesForStudy(studyId)
  }

  /**
   * 切换研究
   */
  const switchStudy = async (studyId: string, updateVisitTime = true) => {
    try {
      // 验证 studyId 格式
      if (!isValidUUID(studyId)) {
        console.error('[StudyContext] Invalid studyId format:', studyId)
        Message.error('无效的研究ID格式')
        return false
      }

      // 确保研究列表已加载
      if (availableStudies.value.length === 0) {
        await loadAvailableStudies()
      }

      // 检查用户是否有该研究的权限
      const studyMembers = availableStudies.value.filter(s => s.studyId === studyId)
      if (studyMembers.length === 0) {
        Message.error('未找到该研究或您无权访问')
        return false
      }

      // 设置当前研究
      console.log('[StudyContext] Setting currentStudyId to:', studyId)
      currentStudyId.value = studyId
      saveToStorage(STORAGE_KEY_STUDY, studyId)

      console.log('[StudyContext] Found studyMembers:', studyMembers.map(m => ({ id: m.id, studyId: m.studyId, roleCode: m.roleCode })))

      // 自动选择默认角色（按 memberCreatedAt 升序取第一个）
      const sortedMembers = [...studyMembers].sort((a, b) =>
        new Date(a.memberCreatedAt).getTime() - new Date(b.memberCreatedAt).getTime()
      )

      // 尝试恢复之前保存的角色
      const savedRoleMemberId = getFromStorage(STORAGE_KEY_ROLE)
      const savedRoleStillValid = savedRoleMemberId && sortedMembers.some(m => m.id === savedRoleMemberId)

      if (savedRoleStillValid) {
        await switchRole(savedRoleMemberId)
      } else {
        await switchRole(sortedMembers[0].id)
      }

      // 更新最后访问时间
      if (updateVisitTime && currentRoleMemberId.value) {
        updateLastVisited(currentRoleMemberId.value).catch(err => {
          console.warn('Failed to update last visited time:', err)
        })
      }

      console.log(`[StudyContext] Switched to study: ${studyId}`)

      // 清理相关查询缓存
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey[0] as string
          const studyRelatedQueries = ['sites', 'study-members', 'dashboard']
          return studyRelatedQueries.includes(queryKey)
        }
      })

      return true
    } catch (error: any) {
      console.error('Failed to switch study:', error)
      Message.error('切换研究失败')
      return false
    }
  }

  /**
   * 切换角色
   */
  const switchRole = async (memberId: string) => {
    const role = availableRoles.value.find(r => r.memberId === memberId)
    if (!role) {
      console.warn('[StudyContext] Role not found:', memberId)
      return false
    }

    currentRoleMemberId.value = memberId
    saveToStorage(STORAGE_KEY_ROLE, memberId)

    console.log(`[StudyContext] Switched to role: ${role.roleDisplay} (${role.roleLevel})`)

    // 如果是中心级角色，加载中心数据并设置默认中心
    if (role.roleLevel === 'SITE' && safeCurrentStudyId.value) {
      await loadSitesForStudy(safeCurrentStudyId.value)

      // 尝试恢复之前保存的中心
      const savedSiteId = getFromStorage(STORAGE_KEY_SITE)
      const savedSiteStillValid = savedSiteId && availableSites.value.some(s => s.id === savedSiteId)

      if (savedSiteStillValid) {
        switchSite(savedSiteId)
      } else if (availableSites.value.length > 0) {
        // 默认选择第一个授权的中心
        switchSite(availableSites.value[0].id)
      } else {
        currentSiteId.value = null
        removeFromStorage(STORAGE_KEY_SITE)
      }
    } else {
      // 研究级角色不需要中心选择
      currentSiteId.value = null
      removeFromStorage(STORAGE_KEY_SITE)
    }

    return true
  }

  /**
   * 切换中心
   */
  const switchSite = (siteId: string) => {
    const site = availableSites.value.find(s => s.id === siteId)
    if (!site) {
      console.warn('[StudyContext] Site not found:', siteId)
      return false
    }

    currentSiteId.value = siteId
    saveToStorage(STORAGE_KEY_SITE, siteId)

    console.log(`[StudyContext] Switched to site: ${site.siteCode}`)
    return true
  }

  /**
   * 从 localStorage 恢复上下文
   */
  const restoreStudyContext = async () => {
    if (!authStore.user?.id) {
      console.warn('No user found, cannot restore study context')
      return
    }

    // 加载可用研究
    await loadAvailableStudies()

    if (availableStudies.value.length === 0) {
      console.log('No studies available for user')
      return
    }

    // 尝试恢复研究
    const savedStudyId = getFromStorage(STORAGE_KEY_STUDY)
    if (savedStudyId) {
      // 验证 savedStudyId 是否为有效的 UUID 格式
      if (!isValidUUID(savedStudyId)) {
        console.warn('Invalid studyId format in localStorage, clearing:', savedStudyId)
        removeFromStorage(STORAGE_KEY_STUDY)
        removeFromStorage(STORAGE_KEY_ROLE)
        removeFromStorage(STORAGE_KEY_SITE)
      } else {
        const hasPermission = availableStudies.value.some(s => s.studyId === savedStudyId)
        if (hasPermission) {
          await switchStudy(savedStudyId, false)
          console.log('Restored study context from localStorage')
          return
        } else {
          removeFromStorage(STORAGE_KEY_STUDY)
          removeFromStorage(STORAGE_KEY_ROLE)
          removeFromStorage(STORAGE_KEY_SITE)
          console.warn('User no longer has permission to access stored study')
        }
      }
    }

    // 默认使用最近访问的研究
    if (uniqueStudies.value.length > 0) {
      const mostRecent = uniqueStudies.value[0]
      await switchStudy(mostRecent.studyId, false)
      console.log('Restored study context to most recent study')
    }
  }

  /**
   * 验证用户对研究的访问权限
   */
  const verifyPermission = async (studyId: string): Promise<boolean> => {
    const authStore = useAuthStore()
    if (!authStore.user?.id) {
      return false
    }

    try {
      const response = await verifyStudyPermission(studyId)
      return response.data.hasPermission
    } catch (error) {
      console.error('Failed to verify study permission:', error)
      return false
    }
  }

  /**
   * 清除上下文
   */
  const clearStudyContext = () => {
    clearInMemoryState()
    clearUserCacheFromStorage(activeUserId.value)

    removeFromStorage(STORAGE_KEY_STUDY)
    removeFromStorage(STORAGE_KEY_ROLE)
    removeFromStorage(STORAGE_KEY_SITE)

    queryClient.invalidateQueries({
      predicate: (query) => {
        const queryKey = query.queryKey[0] as string
        const studyRelatedQueries = ['sites', 'study-members', 'dashboard']
        return studyRelatedQueries.includes(queryKey)
      }
    })

    console.log('Cleared study context')
  }

  const userSignature = computed(() => {
    const user = authStore.user
    if (!user) {
      return null
    }
    return `${user.id}|${user.username}|${user.role}`
  })

  watch(
    () => userSignature.value,
    (nextUserSignature) => {
      const nextUserId = normalizeUserId(authStore.user?.id)
      const previousUserId = normalizeUserId(activeUserId.value)

      if (nextUserSignature === null) {
        if (previousUserId !== null) {
          clearStudyContext()
          setActiveUserId(null)
        }
        return
      }

      clearStudyContext()
      setActiveUserId(nextUserId)
      void restoreStudyContext()
    },
    { immediate: false, flush: 'sync' }
  )

  /**
   * 刷新当前研究数据
   */
  const refreshCurrentStudy = async () => {
    await loadAvailableStudies(true)
  }

  return {
    // State
    availableStudies,
    isLoading,

    // Study Getters
    currentStudy,
    currentStudyId: safeCurrentStudyId, // 导出安全的 UUID 值
    currentStudyCode,
    currentStudyTitle,
    hasCurrentStudy,
    uniqueStudies,
    hasMultipleStudies,

    // Role Getters
    availableRoles,
    currentRole,
    currentRoleCode,
    currentRoleDisplay,
    currentRoleLevel,
    hasMultipleRoles,
    isSiteLevel,

    // Site Getters
    availableSites,
    currentSite,
    currentSiteId,
    currentSiteCode,
    hasMultipleSites,

    // Actions
    loadAvailableStudies,
    switchStudy,
    switchRole,
    switchSite,
    restoreStudyContext,
    verifyPermission,
    clearStudyContext,
    refreshCurrentStudy,
  }
})
